// Spec s9: there is no session API and no orchestration service. The session is
// the tab. This class is what "the session" means - it wires the four pieces
// together and tears them down. Nothing to spawn, nothing to reap.

import type { AudioSource } from '../audio/AudioSource';
import type { ChecklistConfig } from '../config/types';
import { ExtractionLoop, EXTRACTION_INTERVAL_MS, type LoopEvents } from './ExtractionLoop';
import type { FieldState } from './extraction';
import type { ExtractionUsage } from './extractionClient';
import type { Transcriber, TranscriptSegment } from './Transcriber';
import { TranscriptBuffer } from './TranscriptBuffer';
import { debugLog } from '../debug';

export type SessionStatus = 'idle' | 'starting' | 'running' | 'stopping' | 'error';

export type SessionEvents = {
  onStatus?: (status: SessionStatus) => void;
  onState?: (fields: FieldState[]) => void;
  onPartial?: (text: string) => void;
  onFinal?: (segment: TranscriptSegment) => void;
  onInFlight?: (inFlight: boolean) => void;
  onUsage?: (usage: ExtractionUsage & { calls: number }) => void;
  onError?: (message: string) => void;
};

export class Session {
  readonly buffer = new TranscriptBuffer();
  private loop: ExtractionLoop;
  private status: SessionStatus = 'idle';
  private startedAt = 0;
  private frames = 0;
  private peak = 0;
  private lastMeterAt = 0;

  constructor(
    private readonly source: AudioSource,
    private readonly transcriber: Transcriber,
    getConfig: () => ChecklistConfig | null,
    private readonly events: SessionEvents = {},
    intervalMs: number = EXTRACTION_INTERVAL_MS,
  ) {
    const loopEvents: LoopEvents = {
      onState: (fields) => this.events.onState?.(fields),
      onInFlight: (inFlight) => this.events.onInFlight?.(inFlight),
      onUsage: (usage) => this.events.onUsage?.(usage),
      onError: (message) => this.events.onError?.(message),
    };
    this.loop = new ExtractionLoop(this.buffer, getConfig, loopEvents, intervalMs);
  }

  get elapsedMs(): number {
    return this.startedAt ? Date.now() - this.startedAt : 0;
  }

  /** Spec s11: STT seconds, when the transcriber tracks them. */
  get sttSeconds(): number {
    const counter = this.transcriber as Transcriber & { secondsStreamed?: number };
    return counter.secondsStreamed ?? 0;
  }

  get extractionUsage(): ExtractionUsage & { calls: number } {
    return this.loop.usage;
  }

  async start(): Promise<void> {
    if (this.status === 'running' || this.status === 'starting') return;
    this.setStatus('starting');

    try {
      this.transcriber.onPartial((text) => this.events.onPartial?.(text));
      this.transcriber.onFinal((segment) => {
        // Finals only. Extracting from partials produces values that mutate
        // under you (spec s5.1).
        this.buffer.append(segment);
        this.events.onFinal?.(segment);
      });

      await this.transcriber.start();

      this.source.onError((err) => this.events.onError?.(err.message));
      this.source.onFrame((pcm) => {
        this.transcriber.push(pcm);
        this.meter(pcm);
      });
      await this.source.start();

      this.loop.start();
      this.startedAt = Date.now();
      this.setStatus('running');
    } catch (err) {
      this.setStatus('error');
      this.events.onError?.(err instanceof Error ? err.message : String(err));
      await this.stop();
      throw err;
    }
  }

  /** On stop: close the STT socket, tear down the audio graph, discard state. */
  async stop(): Promise<void> {
    if (this.status === 'idle' || this.status === 'stopping') return;
    this.setStatus('stopping');
    this.loop.stop();

    const results = await Promise.allSettled([this.source.stop(), this.transcriber.stop()]);
    for (const result of results) {
      if (result.status === 'rejected') console.warn('[session] teardown', result.reason);
    }

    this.buffer.clear();
    this.startedAt = 0;
    this.setStatus('idle');
  }

  /** Force an extraction pass now (still skipped if one is in flight). */
  refresh(): Promise<void> {
    return this.loop.runOnce();
  }

  /**
   * A heartbeat for the audio path.
   *
   * "Listening" only means the socket opened. It says nothing about whether
   * frames are being produced, or whether they carry anything but silence -
   * and silence reaching Deepgram looks identical to a broken pipeline, since
   * both produce no transcript at all. Peak amplitude separates the two.
   */
  private meter(pcm: Int16Array): void {
    this.frames += 1;
    for (let i = 0; i < pcm.length; i += 8) {
      const value = Math.abs(pcm[i] ?? 0);
      if (value > this.peak) this.peak = value;
    }

    const now = Date.now();
    if (now - this.lastMeterAt < 5000) return;
    this.lastMeterAt = now;
    debugLog.info(
      'audio:',
      `${this.frames} frames`,
      `peak=${this.peak} (${((this.peak / 32768) * 100).toFixed(1)}% of full scale)`,
      this.peak === 0 ? 'SILENCE - nothing is reaching the mixer' : '',
    );
    this.frames = 0;
    this.peak = 0;
  }

  private setStatus(status: SessionStatus): void {
    this.status = status;
    this.events.onStatus?.(status);
  }
}
